/**
 * Billing Work Queue hooks (REV-040/041/042/043/044/048, INT-006).
 *
 * Wraps the Phase 3 revenue/billing surfaces (shapes verified against
 * services/revenue/{routes,schemas}.py and services/integration/schemas.py):
 *   - GET  /api/v1/revenue/billing-queue           → BillingQueue  (summary + by_hospital + invoices)
 *   - GET  /api/v1/charges                          → { items: ChargeListItem[] }
 *   - GET  /api/v1/charges/validation               → ChargeValidationSummary (835/837, REV-041)
 *   - GET  /api/v1/integration/quickbooks/sync-log  → { items: QuickBooksSyncEntry[] } (INT-006)
 *   - POST /api/v1/invoices/{id}:receipt            → InvoiceReceipt (read-receipt, REV-044)
 *
 * The invoice-lifecycle mutations (approve/submit) reuse the same endpoints as
 * useRevenue. List reads tolerate BOTH a bare array and an `{ items: [...] }`
 * envelope and degrade gracefully on 404 (the QuickBooks sync-log route, for
 * example, may not be mounted yet) so the page shows a friendly empty/disabled
 * state rather than crashing.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { RevenueInvoice } from './useRevenue';

/** Headline money figures for the revenue summary bar (REV-042/043/044/048). */
export interface BillingQueueSummary {
  captured_this_week?: number;
  pending_approval?: number;
  submitted?: number;
  write_downs?: number;
}

/** Per-hospital billing rollup row (REV-048). */
export interface BillingQueueHospital {
  tenant_id: string;
  hospital: string;
  captured?: number;
  pending?: number;
  overdue?: number;
}

/** An invoice row in the billing-queue stage tabs (REV-043/044). */
export interface BillingQueueInvoice {
  invoice_id: string;
  hospital: string;
  patient?: string | null;
  amount: number;
  /** Invoice lifecycle status — draft|in_review|approved|submitted|partial|paid|void. */
  stage: string;
  days_outstanding?: number;
}

/** Composed Billing Work Queue payload (wireframe 3). */
export interface BillingQueue {
  summary: BillingQueueSummary;
  by_hospital: BillingQueueHospital[];
  invoices: BillingQueueInvoice[];
}

/** Flattened charge row for the charge-capture grid (REV-040). */
export interface ChargeListItem {
  charge_id: string;
  case_id: string;
  amount: number;
  source: string;
  /** captured | matched | invoiced | exception. */
  status: string;
  captured_at: string;
  variance_flag?: boolean;
}

/** One EHR 835/837 reconciliation discrepancy (REV-041). */
export interface ChargeVariance {
  charge_id: string;
  reason: string;
  delta: number;
}

/** EHR 835/837 reconciliation summary for the billing queue (REV-041). */
export interface ChargeValidationSummary {
  tenant_id: string;
  matched: number;
  variances: ChargeVariance[];
}

/** One QuickBooks sync-log row (INT-006). */
export interface QuickBooksSyncEntry {
  id: string;
  tenant_id?: string;
  /** invoice_push | payment_pull. */
  type: string;
  /** Referenced record (invoice number / payment ref). */
  ref: string;
  /** succeeded | failed | retrying | running. */
  status: string;
  at?: string | null;
  error?: string | null;
}

function unwrap<T>(data: T[] | { items?: T[] } | null | undefined): T[] {
  if (Array.isArray(data)) return data;
  return data?.items ?? [];
}

const EMPTY_QUEUE: BillingQueue = { summary: {}, by_hospital: [], invoices: [] };

interface UseBillingResult {
  queue: BillingQueue;
  charges: ChargeListItem[];
  validation: ChargeValidationSummary | null;
  syncLog: QuickBooksSyncEntry[];
  /** Invoice ids whose read-receipt was recorded this session (REV-044). */
  receipted: Set<string>;
  queueAvailable: boolean;
  chargesAvailable: boolean;
  syncLogAvailable: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  /** Approve a draft invoice (route into finance review). */
  approveInvoice: (invoiceId: string) => Promise<boolean>;
  /** Approve/submit a reviewed invoice (emits INVOICE_APPROVED). */
  submitInvoice: (invoiceId: string) => Promise<boolean>;
  /** Record a read-receipt for a submitted invoice (REV-044). */
  recordReceipt: (invoiceId: string) => Promise<boolean>;
}

/**
 * Loads the billing queue, charges + validation, and the QuickBooks sync log,
 * and exposes per-invoice approve/submit + read-receipt actions. Each fetch
 * degrades independently on 404.
 */
export function useBilling(): UseBillingResult {
  const [queue, setQueue] = useState<BillingQueue>(EMPTY_QUEUE);
  const [charges, setCharges] = useState<ChargeListItem[]>([]);
  const [validation, setValidation] = useState<ChargeValidationSummary | null>(null);
  const [syncLog, setSyncLog] = useState<QuickBooksSyncEntry[]>([]);
  const [receipted, setReceipted] = useState<Set<string>>(new Set());
  const [queueAvailable, setQueueAvailable] = useState(true);
  const [chargesAvailable, setChargesAvailable] = useState(true);
  const [syncLogAvailable, setSyncLogAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);

    const queueP = apiClient
      .get<BillingQueue>('/revenue/billing-queue')
      .then((data) => {
        setQueue({
          summary: data?.summary ?? {},
          by_hospital: unwrap<BillingQueueHospital>(data?.by_hospital),
          invoices: unwrap<BillingQueueInvoice>(data?.invoices),
        });
        setQueueAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setQueueAvailable(false);
          setQueue(EMPTY_QUEUE);
          return;
        }
        throw err;
      });

    const chargesP = apiClient
      .get<ChargeListItem[] | { items: ChargeListItem[] }>('/charges')
      .then((data) => {
        setCharges(unwrap(data));
        setChargesAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setChargesAvailable(false);
          setCharges([]);
          return;
        }
        throw err;
      });

    // Validation summary is a nice-to-have; swallow any failure quietly.
    const validationP = apiClient
      .get<ChargeValidationSummary>('/charges/validation')
      .then((data) => setValidation(data))
      .catch(() => setValidation(null));

    const syncP = apiClient
      .get<QuickBooksSyncEntry[] | { items: QuickBooksSyncEntry[] }>(
        '/integration/quickbooks/sync-log'
      )
      .then((data) => {
        setSyncLog(unwrap(data));
        setSyncLogAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setSyncLogAvailable(false);
          setSyncLog([]);
          return;
        }
        throw err;
      });

    Promise.allSettled([queueP, chargesP, validationP, syncP])
      .then((results) => {
        const failure = results.find(
          (r) => r.status === 'rejected'
        ) as PromiseRejectedResult | undefined;
        if (failure) {
          const reason = failure.reason;
          setError(reason instanceof ApiError ? reason : new ApiError(0, 'ERROR', String(reason)));
        } else {
          setError(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Fold an updated invoice's status back into the queue's invoice list. */
  const applyStatus = useCallback((invoiceId: string, stage: string) => {
    setQueue((q) => ({
      ...q,
      invoices: q.invoices.map((inv) =>
        inv.invoice_id === invoiceId ? { ...inv, stage } : inv
      ),
    }));
  }, []);

  const runLifecycle = useCallback(
    async (invoiceId: string, op: () => Promise<RevenueInvoice>): Promise<boolean> => {
      setIsWorking(true);
      setActionError(null);
      try {
        const invoice = await op();
        if (invoice?.status) applyStatus(invoiceId, invoice.status);
        return true;
      } catch (err: unknown) {
        setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return false;
      } finally {
        setIsWorking(false);
      }
    },
    [applyStatus]
  );

  const approveInvoice = useCallback(
    (invoiceId: string) =>
      runLifecycle(invoiceId, () =>
        apiClient.post<RevenueInvoice>(`/invoices/${encodeURIComponent(invoiceId)}:approve`)
      ),
    [runLifecycle]
  );

  const submitInvoice = useCallback(
    (invoiceId: string) =>
      runLifecycle(invoiceId, () =>
        apiClient.post<RevenueInvoice>(`/invoices/${encodeURIComponent(invoiceId)}:submit`)
      ),
    [runLifecycle]
  );

  /** POST /invoices/{id}:receipt → InvoiceReceipt; tracked in a local set. */
  const recordReceipt = useCallback(async (invoiceId: string): Promise<boolean> => {
    setIsWorking(true);
    setActionError(null);
    try {
      await apiClient.post<{ invoice_id: string; delivered_at: string; channel: string }>(
        `/invoices/${encodeURIComponent(invoiceId)}:receipt`
      );
      setReceipted((prev) => new Set(prev).add(invoiceId));
      return true;
    } catch (err: unknown) {
      setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
      return false;
    } finally {
      setIsWorking(false);
    }
  }, []);

  return {
    queue,
    charges,
    validation,
    syncLog,
    receipted,
    queueAvailable,
    chargesAvailable,
    syncLogAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    approveInvoice,
    submitInvoice,
    recordReceipt,
  };
}
